import { useEffect, useRef, useState, type FormEvent } from "react";
import { ProgressBar } from "./components/ProgressBar";
import { RewardWheel } from "./components/RewardWheel";
import {
  appConfig,
  type AgentId,
  type NfcCardId,
  type Prize,
} from "./config/appConfig";
import { trackActivity } from "./services/activityEvents";
import { maskMemberAccountForDisplay } from "./utils/memberAccountDisplay";

type EntryContext =
  | { status: "demo" }
  | { status: "invalid" }
  | { status: "valid"; agentId: AgentId; cardId: NfcCardId; registrationUrl: string };

type ClaimRecord = {
  serial: string;
  appliedAt: string;
  status: "等待客服處理";
};

type ClaimSaveStatus = "idle" | "saving" | "error";

type PendingClaim = {
  serial: string;
  appliedAt: string;
  prize: Prize;
};

const issuedClaimSerials = new Set<string>();
const MEMBER_ALREADY_CLAIMED_MESSAGE = "此帳號已領取過本次優惠";
const MEMBER_ACCOUNT_FORMAT_ERROR = "手機號碼格式錯誤，請輸入09開頭的10位數字。";
const TAIWAN_MOBILE_PATTERN = /^09\d{8}$/;
const DEV_TEST_MEMBER_ACCOUNT = "1111111111";
const AD_CTA_REVEAL_TIME = 3.85;
const AD_VIDEO_SRC = "/media/yx9935/914go-ad-v1-yx9935-animation-clean-audio-web.mp4";
const AD_FALLBACK_SRC = "/media/yx9935/914go-ad-v1-yx9935-fallback.jpg";

type MemberAccountFormProps = {
  onComplete: (memberAccount: string) => Promise<string | null>;
  registrationUrl: string;
};

function resolveEntryContext(): EntryContext {
  const shortCodeMatch = window.location.pathname.match(/^\/n\/([^/]+)\/?$/);

  if (shortCodeMatch) {
    const agent = appConfig.nfcAgents.find((item) => item.card.shortCode === shortCodeMatch[1]);

    if (!agent) {
      return { status: "invalid" };
    }

    return {
      status: "valid",
      agentId: agent.id,
      cardId: agent.card.id,
      registrationUrl: agent.registrationUrl,
    };
  }

  const params = new URLSearchParams(window.location.search);

  if (!params.has("n")) {
    return { status: "demo" };
  }

  const token = params.get("n");
  const agent = appConfig.nfcAgents.find((item) => item.card.token === token);

  if (!agent) {
    return { status: "invalid" };
  }

  return {
    status: "valid",
    agentId: agent.id,
    cardId: agent.card.id,
    registrationUrl: agent.registrationUrl,
  };
}

function generateClaimSerial(entryContext: EntryContext) {
  const alphabet = "ABCDEFGHJKLMNPQRSTUVWXYZ23456789";
  const agentCode = entryContext.status === "valid"
    ? entryContext.agentId.toUpperCase()
    : "DEMO";

  let serial = "";

  do {
    const randomValues = new Uint32Array(6);
    window.crypto.getRandomValues(randomValues);
    const suffix = Array.from(randomValues, (value) => alphabet[value % alphabet.length]).join("");
    serial = `R-${agentCode}-${suffix}`;
  } while (issuedClaimSerials.has(serial));

  issuedClaimSerials.add(serial);
  return serial;
}

function formatAppliedAt(value: string) {
  return new Intl.DateTimeFormat("zh-TW", {
    dateStyle: "medium",
    timeStyle: "medium",
    hour12: false,
    timeZone: "Asia/Taipei",
  }).format(new Date(value));
}

function isPrize(value: string): value is Prize {
  return (appConfig.prizes as readonly string[]).includes(value);
}

async function writeToClipboard(value: string) {
  if (navigator.clipboard?.writeText) {
    await navigator.clipboard.writeText(value);
    return;
  }

  const textarea = document.createElement("textarea");
  textarea.value = value;
  textarea.style.position = "fixed";
  textarea.style.opacity = "0";
  document.body.appendChild(textarea);
  textarea.select();
  const copied = document.execCommand("copy");
  textarea.remove();

  if (!copied) {
    throw new Error("Clipboard copy failed");
  }
}

function MemberAccountForm({ onComplete, registrationUrl }: MemberAccountFormProps) {
  const phoneInputRef = useRef<HTMLInputElement>(null);
  const [error, setError] = useState("");
  const [isChecking, setIsChecking] = useState(false);

  const submitMemberAccount = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const phone = (phoneInputRef.current?.value ?? "")
      .replace(/\D/g, "")
      .slice(0, 10);
    const isTaiwanMobile = TAIWAN_MOBILE_PATTERN.test(phone);
    const isLocalTest = import.meta.env.DEV
      && phone === DEV_TEST_MEMBER_ACCOUNT;

    if (!isTaiwanMobile && !isLocalTest) {
      setError(MEMBER_ACCOUNT_FORMAT_ERROR);
      return;
    }

    setIsChecking(true);
    setError("");

    try {
      const checkError = await onComplete(phone);

      if (checkError) {
        setError(checkError);
        return;
      }

      trackActivity("registration_self_confirmed", { mode: "live" });
    } finally {
      setIsChecking(false);
    }
  };

  return (
    <div className="mock-success" role="status">
      <div className="step-icon" aria-hidden="true">02</div>
      <h1>完成註冊後確認帳號</h1>
      <p className="account-instruction">完成 YX 註冊後，請輸入剛才註冊使用的台灣手機號碼。請勿輸入密碼。</p>
      <form className="mock-registration-form account-form" onSubmit={submitMemberAccount} autoComplete="off">
        <label>
          <span>會員手機號碼（重新確認並領取優惠）</span>
          <input
            ref={phoneInputRef}
            type="tel"
            inputMode="numeric"
            autoComplete="off"
            maxLength={10}
            defaultValue=""
            placeholder="請輸入09開頭的10位手機號碼"
            aria-invalid={Boolean(error)}
            aria-describedby={error ? "member-account-error" : undefined}
          />
        </label>
        {error && <p id="member-account-error" className="form-error" role="alert">{error}</p>}
        {import.meta.env.DEV && <small>本機測試帳號：1111111111</small>}
        <button className="primary-button" type="submit" disabled={isChecking}>
          {isChecking ? "帳號檢查中…" : "儲存手機號碼並前往輪盤"}
        </button>
      </form>
      <a
        className="secondary-button"
        href={registrationUrl}
        target="_blank"
        rel="noreferrer"
        onClick={() => trackActivity("registration_opened")}
      >重新開啟 YX 註冊頁</a>
      <p className="notice">系統會檢查手機號碼格式；YX註冊狀態仍由客服人工確認。</p>
    </div>
  );
}

export default function App() {
  const [entryContext] = useState<EntryContext>(resolveEntryContext);
  const registrationUrl = entryContext.status === "valid"
    ? entryContext.registrationUrl
    : appConfig.registrationUrl;
  const [step, setStep] = useState(0);
  const [registrationOpened, setRegistrationOpened] = useState(false);
  const [registrationReturnConfirmed, setRegistrationReturnConfirmed] = useState(false);
  const [memberAccount, setMemberAccount] = useState("");
  const [prize, setPrize] = useState<Prize | null>(null);
  const [claimRecord, setClaimRecord] = useState<ClaimRecord | null>(null);
  const [claimSaveStatus, setClaimSaveStatus] = useState<ClaimSaveStatus>("idle");
  const [claimSaveError, setClaimSaveError] = useState("");
  const [copyStatus, setCopyStatus] = useState<"" | "success" | "error">("");
  const [adCtaVisible, setAdCtaVisible] = useState(false);
  const [adHasPlayed, setAdHasPlayed] = useState(false);
  const [adVideoFailed, setAdVideoFailed] = useState(false);
  const [adVideoReady, setAdVideoReady] = useState(false);
  const [adAudioEnabled, setAdAudioEnabled] = useState(false);
  const claimSaveInFlight = useRef(false);
  const pendingClaimRef = useRef<PendingClaim | null>(null);
  const adVideoRef = useRef<HTMLVideoElement>(null);

  useEffect(() => {
    trackActivity("nfc_entry_viewed", { demo: entryContext.status === "demo" });
  }, [entryContext.status]);

  const completeRegistration = async (account: string) => {
    const saveMemberAccount = () => {
      setMemberAccount(account);
      setPrize(null);
      setClaimRecord(null);
      setClaimSaveStatus("idle");
      setClaimSaveError("");
      setCopyStatus("");
      pendingClaimRef.current = null;
      setStep(2);
    };

    saveMemberAccount();
    return null;
  };

  const agentLabel = entryContext.status === "valid"
    ? entryContext.agentId
    : "未指定（DEMO）";
  const cardLabel = entryContext.status === "valid"
    ? entryContext.cardId
    : "未指定（DEMO）";

  const requestPrize = async (): Promise<Prize> => {
    if (!memberAccount || claimSaveInFlight.current) {
      throw new Error("抽獎請求處理中，請稍候");
    }

    claimSaveInFlight.current = true;
    setClaimSaveError("");
    pendingClaimRef.current = null;
    const claimId = generateClaimSerial(entryContext);

    try {
      const response = await fetch("/api/claims", {
        method: "POST",
        headers: {
          "content-type": "application/json",
        },
        body: JSON.stringify({
          claimId,
          campaignId: appConfig.campaignId,
          agentId: agentLabel,
          cardId: cardLabel,
          memberAccount,
        }),
      });
      const body: unknown = await response.json();

      const errorCode = body
        && typeof body === "object"
        && "code" in body
        && typeof body.code === "string"
        ? body.code
        : "";

      if (response.status === 409 || errorCode === "MEMBER_ALREADY_CLAIMED") {
        throw new Error(MEMBER_ALREADY_CLAIMED_MESSAGE);
      }

      if (!response.ok || !body || typeof body !== "object") {
        throw new Error("抽獎失敗，請重新嘗試");
      }

      const returnedPrize = "prizePoints" in body ? String(body.prizePoints) : "";

      if (!isPrize(returnedPrize)) {
        throw new Error("抽獎結果格式錯誤，請重新嘗試");
      }

      const appliedAt = "createdAt" in body && typeof body.createdAt === "string"
        ? body.createdAt
        : new Date().toISOString();

      pendingClaimRef.current = {
        serial: claimId,
        appliedAt,
        prize: returnedPrize,
      };

      return returnedPrize;
    } finally {
      claimSaveInFlight.current = false;
    }
  };

  const completeWheel = (selectedPrize: Prize) => {
    const pendingClaim = pendingClaimRef.current;

    if (!pendingClaim || pendingClaim.prize !== selectedPrize) {
      setClaimSaveStatus("error");
      setClaimSaveError("抽獎結果讀取失敗，請重新嘗試");
      return;
    }

    setPrize(selectedPrize);
    setClaimRecord({
      serial: pendingClaim.serial,
      appliedAt: pendingClaim.appliedAt,
      status: "等待客服處理",
    });
    setClaimSaveStatus("idle");
    setClaimSaveError("");
    setCopyStatus("");
    pendingClaimRef.current = null;
  };

  const saveClaimAndContinue = () => {
    if (!claimRecord || !prize || !memberAccount) return;

    setClaimSaveStatus("idle");
    setStep(3);
  };

  const copyClaimData = async () => {
    if (!claimRecord || !prize || !memberAccount) return;

    setCopyStatus("");

    const claimText = [
      `代理商編號：${agentLabel}`,
      `NFC 名片編號：${cardLabel}`,
      `會員帳號：${memberAccount}`,
      `輪盤點數：${prize} 點`,
      `領獎序號：${claimRecord.serial}`,
      `申請時間：${formatAppliedAt(claimRecord.appliedAt)}`,
      `狀態：${claimRecord.status}`,
    ].join("\n");

    try {
      await writeToClipboard(claimText);
      setCopyStatus("success");
    } catch {
      setCopyStatus("error");
    }
  };

  const restart = () => {
    trackActivity("flow_restarted");
    setStep(0);
    setRegistrationOpened(false);
    setRegistrationReturnConfirmed(false);
    setMemberAccount("");
    setPrize(null);
    setClaimRecord(null);
    setClaimSaveStatus("idle");
    setClaimSaveError("");
    setCopyStatus("");
    pendingClaimRef.current = null;
    setAdCtaVisible(false);
    setAdHasPlayed(false);
    setAdVideoFailed(false);
    setAdVideoReady(false);
    setAdAudioEnabled(false);
  };

  const enableAdAudio = () => {
    const video = adVideoRef.current;

    if (!video) return;

    setAdCtaVisible(false);
    setAdHasPlayed(false);
    video.muted = false;
    video.volume = 1;
    video.currentTime = 0;
    setAdAudioEnabled(true);

    void video.play().catch(() => {
      video.muted = true;
      setAdAudioEnabled(false);
      void video.play();
    });
  };

  return (
    <main className="app-shell">
      <div className="phone-stage">
        <header className="brand-bar">
          <div className="brand-mark">
            <img src="/media/yx9935/branding/yx-ui-logo.svg" alt="YX" />
          </div>
          <div>
            <p className="brand-name">YX GO</p>
            <p className="brand-subtitle">YX NFC轉轉樂</p>
          </div>
          <span className="demo-badge">
            {entryContext.status === "valid" ? entryContext.agentId.toUpperCase() : entryContext.status === "demo" ? "DEMO" : "NFC"}
          </span>
        </header>

        {entryContext.status !== "invalid" && <ProgressBar currentStep={step} />}

        <section className="screen" aria-live="polite">
          {entryContext.status === "invalid" && (
            <div className="centered-panel invalid-entry-panel">
              <div className="invalid-icon" aria-hidden="true">!</div>
              <p className="eyebrow">NFC 辨識失敗</p>
              <h1>無法辨識此 NFC 名片</h1>
              <p className="lead">請確認網址是否完整，或聯絡提供此名片的人員。</p>
            </div>
          )}

          {entryContext.status !== "invalid" && step === 0 && (
            <div className="ad-panel">
              <p className="eyebrow">步驟 1 · YX 專屬活動</p>
              <div className="ad-media">
                {adVideoFailed ? (
                  <img
                    className="ad-video ad-fallback"
                    src={AD_FALLBACK_SRC}
                    alt="YX 專屬活動：輕觸啟動"
                  />
                ) : (
                  <video
                    ref={adVideoRef}
                    className="ad-video"
                    src={AD_VIDEO_SRC}
                    poster={AD_FALLBACK_SRC}
                    muted={!adAudioEnabled}
                    autoPlay={!adHasPlayed}
                    playsInline
                    preload="auto"
                    aria-label="YX 專屬活動動畫"
                    onLoadedData={() => setAdVideoReady(true)}
                    onTimeUpdate={(event) => {
                      if (event.currentTarget.currentTime >= AD_CTA_REVEAL_TIME) {
                        setAdCtaVisible(true);
                      }
                    }}
                    onEnded={() => {
                      setAdHasPlayed(true);
                      setAdCtaVisible(true);
                      trackActivity("ad_viewed");
                    }}
                    onError={() => {
                      setAdVideoFailed(true);
                      setAdVideoReady(false);
                      setAdHasPlayed(true);
                      setAdCtaVisible(true);
                    }}
                  />
                )}
                {adVideoReady && !adAudioEnabled && !adVideoFailed && (
                  <button
                    className="ad-sound-prompt"
                    type="button"
                    onClick={enableAdAudio}
                  >🔊 點一下開啟聲音</button>
                )}
                {adCtaVisible && (
                  <div className="ad-cta" role="group" aria-label="YX 註冊行動按鈕">
                    <p>完成會員註冊後，返回活動頁查看下一步。</p>
                    <a
                      className="primary-button anchor-button ad-cta-button"
                      href={registrationUrl}
                      target="_blank"
                      rel="noreferrer"
                      onClick={() => {
                        setAdHasPlayed(true);
                        setRegistrationOpened(true);
                        setRegistrationReturnConfirmed(false);
                        setStep(1);
                        trackActivity("registration_opened");
                      }}
                    >前往註冊</a>
                  </div>
                )}
              </div>
            </div>
          )}

          {entryContext.status !== "invalid" && step === 1 && (
            <div className="flow-panel">
              <p className="eyebrow">步驟 2 · 會員註冊</p>
              {registrationOpened ? (
                registrationReturnConfirmed ? (
                  <MemberAccountForm
                    onComplete={completeRegistration}
                    registrationUrl={registrationUrl}
                  />
                ) : (
                  <div className="mock-success registration-return">
                    <div className="step-icon" aria-hidden="true">02</div>
                    <h1>完成註冊後返回活動頁</h1>
                    <p>YX 註冊頁已在新分頁開啟。完成會員註冊後，請回到此頁繼續。</p>
                    <button
                      className="primary-button"
                      type="button"
                      onClick={() => setRegistrationReturnConfirmed(true)}
                    >我已完成註冊</button>
                    <a
                      className="secondary-button"
                      href={registrationUrl}
                      target="_blank"
                      rel="noreferrer"
                      onClick={() => trackActivity("registration_opened")}
                    >重新開啟 YX 註冊頁</a>
                    <p className="notice">點擊確認後，將進入既有的會員帳號人工確認流程。</p>
                  </div>
                )
              ) : (
                <>
                  <div className="simple-card">
                    <div className="step-icon">02</div>
                    <h1>開啟 YX 註冊頁</h1>
                    <p>YX 註冊頁會在新分頁開啟，YX 活動頁會繼續保留。</p>
                    <span className="url-chip">{registrationUrl.replace(/^https?:\/\//, "")}</span>
                  </div>
                  <a
                    className="primary-button anchor-button"
                    href={registrationUrl}
                    target="_blank"
                    rel="noreferrer"
                    onClick={() => {
                      setRegistrationOpened(true);
                      setRegistrationReturnConfirmed(false);
                      trackActivity("registration_opened");
                    }}
                  >開啟 YX 註冊頁</a>
                  <p className="notice">本系統不驗證 YX 是否註冊成功，最後由客服人工確認。</p>
                </>
              )}
              <button className="text-button" onClick={() => setStep(0)}>返回廣告頁</button>
            </div>
          )}

          {entryContext.status !== "invalid" && step === 2 && (
            <div className="wheel-panel">
              <h1>YX-轉轉樂</h1>
              <RewardWheel
                requestPrize={requestPrize}
                onComplete={completeWheel}
                onError={(message) => {
                  setClaimSaveStatus("error");
                  setClaimSaveError(message);
                }}
              />
              <button
                className="primary-button"
                disabled={!prize || !claimRecord || claimSaveStatus === "saving"}
                onClick={saveClaimAndContinue}
              >{claimSaveStatus === "saving" ? "資料儲存中…" : "下一步：聯絡客服"}</button>
              {claimSaveStatus === "error" && <p className="form-error" role="alert">{claimSaveError}</p>}
              <button className="text-button" onClick={() => setStep(1)}>返回註冊步驟</button>
            </div>
          )}

          {entryContext.status !== "invalid" && step === 3 && (
            <div className="centered-panel claim-panel">
              <div className="success-icon claim-success-icon" aria-hidden="true">✓</div>
              <p className="eyebrow">步驟 4 · 完整流程完成</p>
              <h1>聯絡客服領取活動資訊</h1>
              <p className="lead claim-instruction">請先複製領獎資料，再開啟 LINE 客服並貼上傳送。</p>
              {claimRecord && (
                <>
                  <div className="summary-card claim-summary">
                    <span>代理商編號</span><strong>{agentLabel}</strong>
                    <span>NFC 名片編號</span><strong>{cardLabel}</strong>
                    <span>會員帳號</span><strong>{maskMemberAccountForDisplay(memberAccount)}</strong>
                    <span>輪盤點數</span><strong>{prize ?? "未測試"} 點</strong>
                    <span>領獎序號</span><strong>{claimRecord.serial}</strong>
                    <span>申請時間</span><strong>{formatAppliedAt(claimRecord.appliedAt)}</strong>
                    <span>狀態</span><strong>{claimRecord.status}</strong>
                  </div>
                  <button className="secondary-button copy-button" onClick={copyClaimData}>複製領獎資料(YX祝你發財)</button>
                  {copyStatus === "success" && <p className="copy-status copy-success" role="status">領獎資料已複製，請傳送給客服</p>}
                  {copyStatus === "error" && <p className="copy-status form-error" role="alert">無法自動複製，請手動選取上方資料。</p>}
                </>
              )}
              {copyStatus === "success" ? (
                <a className="line-button" href={appConfig.lineUrl} target="_blank" rel="noreferrer" onClick={() => trackActivity("support_opened")}>開啟 LINE 客服</a>
              ) : (
                <button className="line-button" type="button" disabled>請先複製領獎資料</button>
              )}
              <p className="notice claim-notice">目前僅為流程測試，不會自動儲值；重新整理後領獎紀錄會清除。</p>
              <button className="secondary-button" onClick={restart}>重新走一次完整流程</button>
            </div>
          )}
        </section>

        <footer>YX NFC</footer>
      </div>
    </main>
  );
}
